/** Words and form reading for the registrar's extra-time records (S4, R1). */
import type { AllowanceRevision, AllowanceTerms } from "@/lib/api/timeAllowances";

export const PCT_MIN = 1;
export const PCT_MAX = 200;

export const allowanceCopy = {
  listLabel: "Extra time",
  listJob: "Every extra-time record for this learner, each with its revisions, newest first; none is ever edited or removed.",
  unavailable: "Extra time could not be read.",
  recordLabel: "Record an allowance",
  recordJob: "Record an extra-time allowance its approver has already approved.",
  notApproval: "Recording is not approval",
  allowancePart: "Allowance",
  recordNote: "Record only what its approver has already approved. Recording is not approval. No diagnosis, condition or medical document is kept here, and the teaching team sees only the percentage and minutes.",
  overlapNote: `Records never overlap: at most one applies to a quiz, whatever its percentage, and a percentage is a whole number from ${PCT_MIN} to ${PCT_MAX}.`,
  recordVerb: "Record the allowance",
  recordCap: "When you record it",
  nextStart: "Timed quizzes it covers use it from their next Start; a run already started keeps what it had.",
  changeLabel: "Correct or revoke a record",
  changeJob: "Correct a record or revoke it; its history is kept.",
  changePart: "Change",
  recordChoice: "Record",
  changeChoice: "Change",
  correctIt: "Correct it",
  revokeIt: "Revoke it",
  correctReason: "Reason for the correction",
  revokeReason: "Reason for revoking it",
  correctCap: "When you correct it",
  revokeCap: "When you revoke it",
  keptRuns: "Runs already started keep the revision they started under.",
  percent: "Extra time, as a whole percentage",
  from: "Effective from",
  until: "Ends (optional)",
  covers: "Covers",
  named: "Named courses only",
  all: "All timed native quizzes in the institution",
  allScope: "All timed native quizzes",
  noCourses: "Not enrolled in a course yet: choose all timed native quizzes, or record it once a course is named.",
  approver: "Approved by",
  reference: "Approval reference",
  enterPercent: "Enter the percentage.",
  percentBounds: `A percentage is a whole number from ${PCT_MIN} to ${PCT_MAX}.`,
  enterFrom: "Enter the effective date.",
  untilBefore: "The end date can't be before the effective date.",
  nameCourse: "Name at least one course, or choose all timed native quizzes.",
  nameApprover: "Name who approved it.",
  enterReference: "Enter the approval's reference.",
  giveCorrectReason: "Give the reason for the correction.",
  giveRevokeReason: "Give the reason for revoking it.",
  revoked: "Revoked",
  inForce: "In force",
  noneInForce: "None in force",
  readyToRecord: "Ready to record",
  readyToChange: "Ready",
  refused: "Nothing was saved.",
  unknownRecord: "Recording the allowance",
  unknownChange: "Saving the change",
  unknownSentence: "We could not confirm whether it was saved. Check before saving again.",
  checkNote: "Check reads the saved record; it never saves twice.",
  action: { recorded: "Recorded", corrected: "Corrected", revoked: "Revoked" } as const,
};
const copy = allowanceCopy;

/** "Mon 24 Aug 2026", read as a calendar day wherever the reader is. */
export function dayWords(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-GB", { timeZone: "UTC", weekday: "short", day: "numeric", month: "short", year: "numeric" }).replace(",", "");
}

export function scopeWords(revision: Pick<AllowanceRevision, "all_quizzes" | "courses">): string {
  return revision.all_quizzes ? copy.allScope : revision.courses.map((course) => course.code || course.title).join(", ");
}

export function spanWords(from: string, until: string | null): string {
  return `from ${dayWords(from)}, ${until ? `until ${dayWords(until)}` : "no end date"}`;
}

/** "50% · ETH-201 · from Mon 24 Aug 2026, no end date" */
export function recordWords(revision: AllowanceRevision): string {
  return `${revision.percent}% · ${scopeWords(revision)} · ${spanWords(revision.effective_from, revision.effective_until)}`;
}

export function revisionWords(revision: AllowanceRevision): string {
  const by = [revision.actor_name, dayWords(revision.created_at)].filter(Boolean).join(", ");
  return [recordWords(revision), `approved by ${revision.approver}, ${revision.reference}`, by, revision.reason || null].filter(Boolean).join(" · ");
}

/** What a registrar types: kept as typed, read into terms only on commit. */
export interface Form {
  percent: string;
  from: string;
  until: string;
  scope: "named" | "all";
  courses: string[];
  approver: string;
  reference: string;
}
export const blankForm: Form = { percent: "", from: "", until: "", scope: "named", courses: [], approver: "", reference: "" };
export const formOf = (revision: AllowanceRevision): Form => ({
  percent: String(revision.percent),
  from: revision.effective_from,
  until: revision.effective_until ?? "",
  scope: revision.all_quizzes ? "all" : "named",
  courses: revision.courses.map((course) => course.id),
  approver: revision.approver,
  reference: revision.reference,
});

export function termsOf(form: Form): { terms: AllowanceTerms; problems: string[] } {
  const percent = form.percent.trim() === "" ? null : Number(form.percent);
  const problems = [
    ...(percent === null ? [copy.enterPercent] : !Number.isInteger(percent) || percent < PCT_MIN || percent > PCT_MAX ? [copy.percentBounds] : []),
    ...(form.from ? [] : [copy.enterFrom]),
    ...(form.from && form.until && form.until < form.from ? [copy.untilBefore] : []),
    ...(form.scope === "named" && !form.courses.length ? [copy.nameCourse] : []),
    ...(form.approver.trim() ? [] : [copy.nameApprover]),
    ...(form.reference.trim() ? [] : [copy.enterReference]),
  ];
  return {
    terms: {
      percent: percent ?? 0,
      effective_from: form.from,
      effective_until: form.until || null,
      all_quizzes: form.scope === "all",
      course_ids: form.scope === "all" ? [] : form.courses,
      approver: form.approver.trim(),
      reference: form.reference.trim(),
    },
    problems,
  };
}
